import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 3.1 visual preview at /preview/lesson/3.1
 *
 * "From one prompt to a workflow" — Week 3 opener.
 *
 * Week 2 taught the five-stage loop on one task. Week 3 teaches composition:
 * one prompt becomes a chain of prompts, with you in the seat at every handoff.
 *
 * Promotes "Chaining" as the new vocab term with the full 4-block treatment.
 * Your-turn is "Spot the chain": three messy tasks, student picks the right
 * decomposition (or walk-away).
 */

export default function LessonPreviewPage3_1() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 3 · Building with AI · Lesson 3.1
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        From one prompt to a workflow
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Pattern · Judgment</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">3 decomposition drills</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Two students. Same college essay. Six weeks. One night vs. six sessions."
      accent="amber"
    >
      <p>
        Common App essay. 650 words. Both students start with the same prompt
        their counselor handed them. Both submit on time. Both get into a
        college they wanted. The work on the way there does not look the same
        at all.
      </p>

      <StudentSplit />

      <p className="mt-4">
        Student A used AI as a vending machine. One giant prompt. One giant
        answer. One frantic copy-paste at 11 p.m. Student B used AI as a chain.
        Six sessions, four steps, a different prompt each time. Same word
        count. Wildly different essay. The difference is not effort. It is
        shape.
      </p>
      <p className="text-text">
        Week 2 taught you to run the loop on one task. Week 3 teaches you to
        run the loop on work that has steps. Long work. Real work. The kind
        you actually do.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time · Week 2 capstone"
      eyebrow="Three questions on the five-stage loop. No looking back."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> Name the five
          stages of the Week 2 loop in order.
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> What does Stage 1
          (Think first) actually look like, in one sentence?
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> If a draft contains
          a claim you did not put in the prompt, what zone is it in and what
          do you do?
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        If any of those felt fuzzy, run 2.5 again before you start this lesson.
        Week 3 stacks loops on top of loops. If one loop is shaky, six of them
        in a row will not be better.
      </p>
    </Section>
  );
}

// ---------- Teaching ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="Teaching · Chaining the loop"
      eyebrow="One giant prompt fails. A chain of small prompts works. Here is why and how."
    >
      <Subhead n="1" title="One prompt vs. a chain" />
      <p>
        Most students try to do real work in one prompt. Type the whole
        problem, hope the AI figures it out, paste whatever comes back. The
        bigger the task, the worse this gets. The AI is not stupid. It just
        cannot read your mind across five jobs at once.
      </p>
      <OneVsChain />
      <p className="mt-3 text-sm text-muted">
        The one-shot version is faster to type and worse at almost everything.
        The chain version is slower to start and better at almost everything.
        Real work is the chain.
      </p>

      <Subhead n="2" title="The thing you have to name" />
      <p>
        Before we talk about how to build a chain, you need a word for the
        move. Without the word, every multi-step task feels like five tasks.
        With the word, it is one shape, repeated.
      </p>
      <VocabCard
        term="Chaining"
        definition="Using the output of one AI step as the input to the next, on purpose, with you reading and editing at every handoff. One task becomes a sequence of small loops. The chain is yours; the AI just helps with each link."
        analogy="Cooking a real meal is a chain: prep, sauce, sear, plate. You taste between steps. If you skipped tasting and dumped everything in one pot, you would not get faster food. You would get worse food."
        hook="Small loops in order. You taste between every one."
        matters="One giant prompt is how students get generic essays, wrong answers, and slop they have to throw away. Chaining is how they get work they would put their name on. Same AI. Different shape."
      />

      <Subhead n="3" title="The shape of a good chain" />
      <p>
        Every chain that matters has the same four moves. The names change.
        The shape does not. Research what you do not know. Draft what you do
        know. Audit what you wrote. Ship what survives. Each move is its own
        small loop from Week 2.
      </p>
      <ChainShape />
      <p className="mt-3 text-sm text-muted">
        Notice the lines between steps. Those are not decoration. Those are
        the handoffs. The handoff is where 80 percent of the work happens and
        where 100 percent of the mistakes get made.
      </p>

      <Subhead n="4" title="The handoff move" />
      <p>
        The space between two steps in a chain is where you stay in the seat
        or where you disappear. Three rules. Do them every time. No
        exceptions.
      </p>
      <Handoffs />
      <p className="mt-3 text-sm text-muted">
        Pattern: the AI does the work inside each step. You do the work
        between steps. If you stop doing the between-work, the chain becomes
        one long prompt with extra buttons, and you get one long generic
        answer.
      </p>
    </Section>
  );
}

// ---------- Your turn ----------

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn · Spot the chain"
      eyebrow="Three messy tasks. Pick the decomposition that fits."
    >
      <p>
        Three real things a student your age would actually do. For each one,
        three possible ways to break it into steps. Pick the one that looks
        like a real chain. Some of the wrong answers look right at first
        glance. Read carefully.
      </p>

      <ChainDrill
        n={1}
        scenario="Build your college list. You know roughly what kind of school you want (mid-size, near a city, strong CS), but you have no list yet. You want about 10 schools across reach, target, and likely."
        options={[
          {
            id: 'a',
            label: 'Option A · One shot',
            steps: [
              'Ask AI: "Give me a list of 10 colleges I should apply to."',
            ],
          },
          {
            id: 'b',
            label: 'Option B · Real chain',
            steps: [
              'Research · Ask AI for criteria a strong CS program has that most students miss (research depth, recruiting, advising load).',
              'Draft · You list 15 schools that match. AI suggests 10 more you did not name.',
              'Audit · For each school, you cross-check admit rate, location, and one specific program detail. AI cannot verify these; you verify.',
              'Ship · You cut the list to 10. Reach, target, likely. You can defend why each school is on it.',
            ],
          },
          {
            id: 'c',
            label: 'Option C · Fake chain',
            steps: [
              'Ask AI for 10 reach schools.',
              'Ask AI for 10 target schools.',
              'Ask AI for 10 likely schools.',
              'Pick the ones that sound good.',
            ],
          },
        ]}
        correct="b"
        verdict="Option B. The chain mixes what AI is good at (criteria, suggesting names you did not know) with what only you can do (knowing what you actually want, verifying real numbers, defending the final list). Option C looks like a chain but is three vending-machine prompts in a row. No handoff work. No verification. You end up applying to schools you cannot defend in a single interview question."
      />

      <ChainDrill
        n={2}
        scenario="Group project. Five people. Topic: climate change. The project has to include a written argument, three sources, and a 10-minute presentation. Due in 12 days."
        options={[
          {
            id: 'a',
            label: 'Option A · Real chain',
            steps: [
              'Research · Each group member reads one credible source. AI helps you find them but you read them.',
              'Draft · Your group writes a one-paragraph argument together. AI is a sparring partner: where is this weak?',
              'Audit · You assign sections, draft them individually, then meet to cut what is generic.',
              'Ship · Slides last. The argument drives the slides, not the other way around.',
            ],
          },
          {
            id: 'b',
            label: 'Option B · One shot',
            steps: [
              'Ask AI: "Write a group project on climate change with three sources and a 10-minute presentation."',
            ],
          },
          {
            id: 'c',
            label: 'Option C · Reverse chain',
            steps: [
              'Have AI make slides first.',
              'Write the script to match the slides.',
              'Find sources that back up what the slides already say.',
              'Submit.',
            ],
          },
        ]}
        correct="a"
        verdict="Option A. The argument comes first. The slides come last. Option C is the most common bad shape and the easiest one to fall into: you let the slides decide what the project is, then back-fill sources to fit. That is how you end up citing things that do not actually support your claim. Build the argument, then dress it."
      />

      <ChainDrill
        n={3}
        scenario="AP exam in three weeks. AP Calculus AB. You know your weak spots are related rates and the Fundamental Theorem of Calculus. You have 60 minutes a day to study."
        options={[
          {
            id: 'a',
            label: 'Option A · Generic chain',
            steps: [
              'Ask AI for a generic 21-day AP Calc study plan.',
              'Follow the plan.',
              'Take the exam.',
            ],
          },
          {
            id: 'b',
            label: 'Option B · Real chain',
            steps: [
              'Research · Take one timed practice section. See where you actually lose points.',
              'Draft · Build a 21-day plan around the two weak spots, not the whole curriculum.',
              'Audit · Each week, redo a past FRQ. If your score is not moving on the weak spots, change the plan.',
              'Ship · Last 3 days: full practice exam, then rest. Not cram. Rest.',
            ],
          },
          {
            id: 'c',
            label: 'Option C · One shot',
            steps: [
              'Ask AI to write a 21-day plan that covers everything.',
              'Print it.',
            ],
          },
        ]}
        correct="b"
        verdict="Option B. The diagnostic comes first. Studying related rates harder when you are losing points on integrals is a way to spend 21 days feeling productive and learning nothing new. The real chain measures, plans, re-measures, and adjusts. Option A is a fake chain because the plan never reacts to evidence. Option C is a fancy way to study things you already knew."
      />
    </Section>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const items = [
    {
      q: 'Why does one giant prompt fail on work that has multiple steps?',
      a: 'Because the AI cannot read your mind across several jobs at once. It blends them, smooths the edges, and produces something generic that touches every step shallowly. A chain forces each step to be specific, with its own prompt and its own audit.',
    },
    {
      q: 'Define chaining in one sentence.',
      a: 'Using the output of one AI step as the input to the next, on purpose, with you reading and editing at every handoff so one big task becomes a sequence of small loops.',
    },
    {
      q: 'Name the four moves in the shape of a good chain.',
      a: 'Research what you do not know. Draft what you do know. Audit what you wrote. Ship what survives. Each one is its own Week 2 loop.',
    },
    {
      q: 'What are the three handoff rules between steps in a chain?',
      a: 'Read every output before you pass it forward. Edit before you pass it; do not pass slop down the chain. Re-think the next prompt based on what the last step actually produced, not what you hoped it would produce.',
    },
    {
      q: 'In the cold open, both students submit the same essay length. What is the difference six weeks later?',
      a: 'Student A has an essay and one night of frantic work. Student B has an essay and six sessions of real thinking: their school research, their thesis, their audit, their edits. Same word count, very different result. The chain is the difference.',
    },
  ];

  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Five questions. Answer first. Then reveal."
    >
      <div className="space-y-3">
        {items.map((it, i) => (
          <QuickCheckItem key={i} n={i + 1} q={it.q} a={it.a} />
        ))}
      </div>
    </Section>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Run it any time work has more than one step."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Lesson 3.1 · Lock card · Chaining the loop
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          Long work is a chain of short loops. Four moves. One handoff between
          every pair.
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <ChainTile
            n="01"
            label="Research"
            q="What do I not know yet?"
            sub="Read enough to ask a smart question. AI helps you find sources. You read them."
          />
          <ChainTile
            n="02"
            label="Draft"
            q="What do I already think?"
            sub="Get something on paper. Yours first when possible. AI as drafter or sparring partner."
          />
          <ChainTile
            n="03"
            label="Audit"
            q="What is wrong with this?"
            sub="Trust ladder on every claim. Find the generic line. Cut what you cannot defend."
          />
          <ChainTile
            n="04"
            label="Ship"
            q="What goes out under my name?"
            sub="Last edit is yours. Voice, tone, final call. The chain ends with a decision."
          />
        </div>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The handoff rule
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text/90">
            Between every step: read what came out, edit before you pass it
            forward, re-think the next prompt based on what you actually have,
            not what you hoped. The chain breaks at the handoffs, not at the
            prompts.
          </p>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          Research. Draft. Audit. Ship. Read between every step. Your hands on
          the chain.
        </p>
      </div>
    </Section>
  );
}

// ---------- Custom components for 3.1 ----------

function StudentSplit() {
  return (
    <div className="my-6 grid gap-4 lg:grid-cols-2">
      <StudentCard
        variant="bad"
        label="Student A"
        sub="One night. 90 minutes."
        steps={[
          'Reads the Common App prompt at 9 p.m. the night before the early deadline.',
          'Opens AI. Pastes the entire prompt plus a paragraph about themselves.',
          'Reads the response once. Adjusts three sentences so it sounds less robotic.',
          'Submits at 11:47 p.m. The essay is on time. The essay is also a Common App essay an admissions reader has read forty times.',
        ]}
        verdict="What got built: nothing they will keep. The essay exists. Six months from now they will not remember writing it because they did not write it."
      />
      <StudentCard
        variant="good"
        label="Student B"
        sub="Six weeks. Six 30-minute sessions."
        steps={[
          'Week 1 · Researches the school. Reads three news stories about it. Asks AI to summarize a faculty member they admire. They cross-check.',
          'Week 3 · Drafts a thesis in two sentences: the moment in their life that shaped how they see the world. Asks AI to push back on it. Rewrites.',
          'Week 4 · Drafts the full essay themselves. Hands it to AI as editor: where is this generic, what specific detail is missing, which line sounds like every other applicant.',
          'Week 6 · Last pass on voice. Reads it aloud. Submits. The essay sounds like them because they wrote it. AI was a teammate at four handoffs, not the writer at one.',
        ]}
        verdict="What got built: an essay and a thinking pattern. The essay is theirs. The skill of breaking long work into a chain of small moves is theirs. They will use that shape for the next ten years."
      />
    </div>
  );
}

function StudentCard({
  variant,
  label,
  sub,
  steps,
  verdict,
}: {
  variant: 'good' | 'bad';
  label: string;
  sub: string;
  steps: string[];
  verdict: string;
}) {
  const styles =
    variant === 'good'
      ? {
          border: 'border-emerald-500/30',
          bg: 'bg-emerald-500/[0.04]',
          badgeBg: 'bg-emerald-500/20',
          badgeText: 'text-emerald-300',
          numText: 'text-emerald-300',
          verdictBorder: 'border-emerald-500/30',
          verdictBg: 'bg-emerald-500/[0.05]',
        }
      : {
          border: 'border-rose-500/30',
          bg: 'bg-rose-500/[0.04]',
          badgeBg: 'bg-rose-500/20',
          badgeText: 'text-rose-300',
          numText: 'text-rose-300',
          verdictBorder: 'border-rose-500/30',
          verdictBg: 'bg-rose-500/[0.05]',
        };
  return (
    <div className={`rounded-xl border ${styles.border} ${styles.bg} p-5`}>
      <div className="flex items-baseline justify-between gap-3">
        <span
          className={`inline-block w-fit shrink-0 rounded-md ${styles.badgeBg} px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${styles.badgeText}`}
        >
          {label}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
          {sub}
        </span>
      </div>
      <ol className="mt-4 space-y-2.5">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm text-text/90">
            <span
              className={`mt-0.5 font-mono text-[10px] uppercase tracking-widest ${styles.numText}`}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="flex-1 leading-relaxed">{s}</span>
          </li>
        ))}
      </ol>
      <div
        className={`mt-4 rounded-md border ${styles.verdictBorder} ${styles.verdictBg} px-4 py-3`}
      >
        <p
          className={`font-mono text-[10px] uppercase tracking-widest ${styles.badgeText}`}
        >
          What got built
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-text/90">{verdict}</p>
      </div>
    </div>
  );
}

function OneVsChain() {
  return (
    <div className="my-6 grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/[0.04] p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
          One giant prompt
        </p>
        <p className="mt-2 text-sm italic text-text/80">
          “Plan a fundraiser for the debate team. We need 800 dollars in six
          weeks. Give me a full plan with budget, marketing, sponsors, and a
          timeline.”
        </p>
        <div className="mt-4 space-y-2 text-sm text-text/90">
          <p>
            <span className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
              What comes back ·{' '}
            </span>
            A generic fundraiser template. Bake sale. Car wash. A budget that
            assumes nothing about your school. A timeline that ignores when
            your tournaments are.
          </p>
          <p>
            <span className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
              Why it fails ·{' '}
            </span>
            The AI does not know your school, your team, your tournament
            schedule, or who would actually donate. It guessed at all of them
            in one shot.
          </p>
        </div>
      </div>
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
          A chain of four prompts
        </p>
        <ol className="mt-2 space-y-2 text-sm text-text/90">
          <li>
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              01 · Research ·{' '}
            </span>
            “Common fundraisers for a 30-person high school club, ranked by
            dollars per hour of work.”
          </li>
          <li>
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              02 · Draft ·{' '}
            </span>
            “Given our team has tournaments weeks 2 and 5, draft a 6-week
            timeline using a coffee cart at home games (weeks 1, 3, 4, 6).”
          </li>
          <li>
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              03 · Audit ·{' '}
            </span>
            “Spot the weak assumptions in this plan. Where am I being
            optimistic about volume or pricing?”
          </li>
          <li>
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              04 · Ship ·{' '}
            </span>
            You write the version you bring to the team meeting. Specifics,
            owners, dates.
          </li>
        </ol>
        <p className="mt-4 text-sm text-text/90">
          <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
            Why it works ·{' '}
          </span>
          Each step has one job. Each step gets your eyes on it. The plan ends
          up specific to your school because you put your school into the
          chain at every handoff.
        </p>
      </div>
    </div>
  );
}

function ChainShape() {
  const steps = [
    {
      n: '01',
      label: 'Research',
      q: 'What do I not know yet?',
      who: 'AI suggests sources, frameworks, criteria.',
      you: 'You read. You decide what is credible. You take notes by hand or in your own words.',
      mode: 'Tutor or Sparring partner',
    },
    {
      n: '02',
      label: 'Draft',
      q: 'What do I already think?',
      who: 'AI gives you a starting structure or a rough first version.',
      you: 'You react. You rewrite in your voice. The draft is yours, not the AI’s.',
      mode: 'Drafter or Sparring partner',
    },
    {
      n: '03',
      label: 'Audit',
      q: 'What is wrong with this?',
      who: 'AI points at weak claims, generic lines, missing detail.',
      you: 'You apply the trust ladder. Cross-check facts. Cut anything you cannot defend.',
      mode: 'Editor',
    },
    {
      n: '04',
      label: 'Ship',
      q: 'What goes out under my name?',
      who: 'AI helps with polish, format, the last 10 percent.',
      you: 'You make the final call. Last edit is yours. The decision is yours.',
      mode: 'Editor or alone',
    },
  ];
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="grid grid-cols-[80px_minmax(0,1fr)] border-b border-line bg-elevated/40 px-5 py-2.5 text-[10px] font-mono uppercase tracking-widest text-muted">
        <span>Step</span>
        <span>Question · AI does · You do · Best mode</span>
      </div>
      {steps.map((s, i) => (
        <div
          key={s.label}
          className={`grid grid-cols-[80px_minmax(0,1fr)] gap-4 px-5 py-4 ${
            i < steps.length - 1 ? 'border-b border-line' : ''
          }`}
        >
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
              {s.n}
            </p>
            <p className="mt-1 font-semibold text-text">{s.label}</p>
          </div>
          <div className="space-y-2 text-sm text-text/90">
            <p className="italic text-text/80">{s.q}</p>
            <p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                AI ·{' '}
              </span>
              {s.who}
            </p>
            <p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-amber">
                You ·{' '}
              </span>
              {s.you}
            </p>
            <p>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                Mode ·{' '}
              </span>
              <span className="text-text/80">{s.mode}</span>
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Handoffs() {
  const rules = [
    {
      label: 'Read every output',
      sub: 'Before passing forward',
      body: 'Read what the AI actually produced, not what you asked for. If you skim, you will pass mistakes down the chain and never see them again until they are in the final.',
    },
    {
      label: 'Edit before you pass',
      sub: 'No slop forward',
      body: 'Cut what is wrong, vague, or off-topic. The next step inherits whatever you keep. Sloppy step two means a shaky step four. Clean the link before you forge the next one.',
    },
    {
      label: 'Re-think the next prompt',
      sub: 'React to reality',
      body: 'Do not run the next step the way you planned it last week. Run it the way it needs to go given what the last step actually gave you. The chain reacts. A plan that ignores the last result is not a chain.',
    },
  ];
  return (
    <div className="my-6 grid gap-3 sm:grid-cols-3">
      {rules.map((r, i) => (
        <div
          key={r.label}
          className="rounded-lg border border-line bg-surface p-4"
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
              {String(i + 1).padStart(2, '0')}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
              {r.sub}
            </span>
          </div>
          <p className="mt-2 font-semibold text-text">{r.label}</p>
          <p className="mt-1.5 text-sm leading-relaxed text-text/90">{r.body}</p>
        </div>
      ))}
    </div>
  );
}

type ChainOption = {
  id: string;
  label: string;
  steps: string[];
};

function ChainDrill({
  n,
  scenario,
  options,
  correct,
  verdict,
}: {
  n: number;
  scenario: string;
  options: ChainOption[];
  correct: string;
  verdict: string;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const isCorrect = picked === correct;
  const wasWrong = picked !== null && !isCorrect;
  const pickedOption = options.find((o) => o.id === picked);
  const correctOption = options.find((o) => o.id === correct);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Drill {n} · The task
        </p>
        <p className="mt-1.5 text-sm text-text/90">{scenario}</p>
      </div>
      <div className="space-y-3 px-5 py-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Pick the decomposition
        </p>
        {options.map((opt) => {
          const selected = picked === opt.id;
          const isRight = picked && correct === opt.id;
          const isWrong = picked && selected && correct !== opt.id;
          const borderClass = isRight
            ? 'border-emerald-500/50 bg-emerald-500/[0.05]'
            : isWrong
            ? 'border-rose-500/50 bg-rose-500/[0.05]'
            : selected
            ? 'border-amber bg-amber/[0.06]'
            : 'border-line bg-ink/40 hover:border-amber/50';
          return (
            <button
              key={opt.id}
              onClick={() => setPicked(opt.id)}
              className={`block w-full rounded-lg border p-4 text-left transition-colors ${borderClass}`}
            >
              <p
                className={`font-mono text-[11px] uppercase tracking-widest ${
                  selected ? 'text-amber' : 'text-muted'
                }`}
              >
                {opt.label}
              </p>
              <ol className="mt-2 space-y-1.5">
                {opt.steps.map((s, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-text/90">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="flex-1 leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>
            </button>
          );
        })}

        {wasWrong && (
          <div className="mt-3 rounded-md border border-rose-500/40 bg-rose-500/[0.06] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-rose-300">
              Not this one · {pickedOption?.label}
            </p>
            <p className="mt-1.5 text-sm text-text/85">
              That is not the right decomposition. See the correct answer below.
            </p>
          </div>
        )}
        {picked && (
          <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/[0.04] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              {wasWrong ? 'The right decomposition' : 'Correct'} · {correctOption?.label}
            </p>
            <p className="mt-1.5 text-sm text-text/90">{verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ChainTile({
  n,
  label,
  q,
  sub,
}: {
  n: string;
  label: string;
  q: string;
  sub: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-5">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
          {n}
        </span>
        <span className="inline-block w-fit shrink-0 rounded-md bg-amber/20 px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          {label}
        </span>
      </div>
      <p className="mt-3 text-base font-semibold text-text">{q}</p>
      <p className="mt-2 text-sm leading-relaxed text-text/90">{sub}</p>
    </div>
  );
}

// ---------- Next up ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 3.2
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            The research step: finding what is true
          </h3>
          <p className="mt-2 text-sm text-muted">
            The first link in every chain that matters. How to ask AI to surface
            what you do not know without taking what it says on faith. Sources,
            cross-checks, and the difference between a search and a citation.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

// ---------- Shared primitives ----------

function Subhead({ n, title }: { n: string; title: string }) {
  return (
    <h3 className="mt-8 flex items-baseline gap-3 text-lg font-semibold text-text">
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {n}
      </span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  hook,
  matters,
}: {
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
}) {
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Vocab · 3.1 · New term
        </p>
        <h4 className="mt-1 text-xl font-semibold text-text">{term}</h4>
      </div>
      <div className="space-y-4 px-5 py-5">
        {!revealed && (
          <>
            <p className="text-sm text-muted">
              Write your own one-sentence definition first. Then reveal.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Your definition of "${term}"...`}
              className="h-20 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
            />
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal definition →
            </button>
          </>
        )}
        {revealed && (
          <div className="space-y-3">
            <Block label="Definition" body={definition} />
            <Block label="Analogy" body={analogy} accent="amber" />
            <Block label="Memory hook" body={hook} />
            <Block label="Why it matters" body={matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = 'plain',
}: {
  label: string;
  body: string;
  accent?: 'plain' | 'amber';
}) {
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        accent === 'amber'
          ? 'border-amber/40 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent === 'amber' ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: '2.5 review' },
    { id: 'sec-teaching', label: 'Chaining the loop' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
